/**
 * C3-B.2 · Controllo Workflow Mensile (UI)
 *
 * Mostra stato corrente compatto e le sole azioni consentite dal ruolo.
 * Nessun controllo di sicurezza: la UI si limita a nascondere azioni non
 * previste dalla matrice backend (che rimane l'unica autorità).
 *
 * Props:
 *   companyId, employeeId, year, month  → coordinate del workflow
 *   onStateChange(newState)             → callback dopo transizione
 *   compact = true                      → rendering inline
 */
import React, { useCallback, useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { SuiteBadge, SuiteButton } from "@/components/suite";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Send, CheckCircle2, AlertTriangle, Lock, Unlock, History } from "lucide-react";
import { formatDateTimeIT } from "@/foundation/formatting";

// Diciture ufficiali stato (obbligatorie da specifica C3-B.2 §3)
export const STATE_LABEL = {
  draft: "Bozza",
  sent_to_company: "Inviato all'azienda",
  to_correct: "Da correggere",
  approved: "Approvato",
  closed: "Chiuso",
};

const STATE_VARIANT = {
  draft: "default",
  sent_to_company: "info",
  to_correct: "warning",
  approved: "success",
  closed: "muted",
};

const EDITABLE = new Set(["draft", "to_correct"]);
export function isEditableState(s) { return EDITABLE.has(s); }

// Normalizzazione ruolo (allinea a backend _LEGACY_TO_NEW)
function normalizeRole(u) {
  const r = (u?.role || "").toLowerCase();
  if (r === "admin") return "super_admin";
  if (r === "client") return "company_admin";
  if (r === "responsabile") return "company_responsible";
  return r;
}

// Matrice UI (mostrare/nascondere). L'enforcement effettivo è backend.
function canExecute(roleNorm, action) {
  if (roleNorm === "super_admin")
    return ["complete_by_studio", "reopen", "close"].includes(action);
  if (roleNorm === "studio_operator") return action === "complete_by_studio";
  if (roleNorm === "company_admin" || roleNorm === "company_responsible") {
    return [
      "request_correction", "approve", "close", "reopen",
      "complete_by_company", "submit_correction",
    ].includes(action);
  }
  if (roleNorm === "employee") {
    // v1.2.1: dipendente puo' inviare, ritirare l'invio e reinviare.
    return ["send", "withdraw", "submit_correction"].includes(action);
  }
  return false;
}

const REASON_REQUIRED = new Set(["request_correction", "reopen", "submit_correction"]);

export default function MonthWorkflowControl({
  companyId, employeeId, year, month,
  onStateChange, hasUnsavedChanges, refreshKey,
}) {
  const { user } = useAuth();
  const roleNorm = normalizeRole(user);
  const [state, setState] = useState(null);
  const [version, setVersion] = useState(null);
  const [workflowId, setWorkflowId] = useState(null);
  const [snapshotHash, setSnapshotHash] = useState(null);
  const [snapshotVersion, setSnapshotVersion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogAction, setDialogAction] = useState(null);
  const [reason, setReason] = useState("");
  const [events, setEvents] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  const loadState = useCallback(async () => {
    if (!companyId || !employeeId || !year || !month) { setState(null); return; }
    try {
      const r = await api.get("/workflow/months/state", {
        params: { company_id: companyId, employee_id: employeeId, year, month },
      });
      setState(r.data.current_state);
      setVersion(r.data.version);
      setWorkflowId(r.data.id);
      setSnapshotHash(r.data.snapshot_hash || null);
      setSnapshotVersion(r.data.snapshot_version || 0);
      onStateChange?.(r.data.current_state, r.data);
    } catch (e) {
      // 403 su employee di altra azienda → semplicemente nasconde controls
      if (e?.response?.status !== 403) toast.error(apiErr(e));
      setState(null);
    }
  }, [companyId, employeeId, year, month, onStateChange, refreshKey]);

  useEffect(() => { loadState(); }, [loadState]);

  const loadEvents = useCallback(async () => {
    if (!workflowId) return;
    try {
      const r = await api.get("/workflow/months/events",
        { params: { workflow_id: workflowId } });
      setEvents(r.data.events || []);
    } catch (e) { toast.error(apiErr(e)); }
  }, [workflowId]);

  const openDialog = (action) => {
    if (hasUnsavedChanges && hasUnsavedChanges()) {
      const ok = window.confirm(
        "Ci sono modifiche non salvate. Continuando andranno perse. Procedere?");
      if (!ok) return;
    }
    setReason("");
    setDialogAction(action);
  };

  const doTransition = async (action, reasonText) => {
    if (workflowId == null || version == null) {
      toast.error("Stato del workflow non disponibile");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/workflow/months/transition", {
        workflow_id: workflowId,
        action,
        expected_version: version,
        ...(reasonText ? { reason: reasonText } : {}),
      });
      setState(r.data.current_state);
      setVersion(r.data.version);
      setSnapshotHash(r.data.snapshot_hash || null);
      setSnapshotVersion(r.data.snapshot_version || snapshotVersion);
      toast.success(`Stato aggiornato: ${STATE_LABEL[r.data.current_state]}`);
      onStateChange?.(r.data.current_state, r.data);
      setDialogAction(null);
    } catch (e) {
      const st = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (st === 409) {
        toast.error(
          "Il mese è stato aggiornato da un altro utente. Ricarico i dati — ricontrolla prima di procedere.",
          { duration: 8000 });
        await loadState();
      } else if (st === 400 && detail?.toString?.().includes?.("Motivazione")) {
        toast.error("Motivazione obbligatoria");
      } else {
        toast.error(apiErr(e));
      }
    } finally { setLoading(false); }
  };

  if (!state) return null;

  const actions = [];
  if (roleNorm === "employee") {
    // v1.2.1: pulsanti operativi per il dipendente
    if (state === "draft" && canExecute(roleNorm, "send"))
      actions.push({ key: "send", label: "Invia il cartellino", icon: Send, variant: "accent" });
    if (state === "sent_to_company" && canExecute(roleNorm, "withdraw"))
      actions.push({ key: "withdraw", label: "Ritira invio", icon: Unlock, variant: "warning" });
    if (state === "to_correct" && canExecute(roleNorm, "submit_correction"))
      actions.push({ key: "submit_correction", label: "Reinvia dopo correzione", icon: Send, variant: "accent" });
  } else {
    if ((state === "draft" || state === "to_correct") &&
        canExecute(roleNorm, "complete_by_studio"))
      actions.push({
        key: "complete_by_studio",
        label: "Concludi cartellino",
        icon: Lock,
        variant: "outline",
        className: "!border-suite-primary !text-suite-primary hover:!bg-suite-primary-light",
      });
    if ((state === "draft" || state === "to_correct") &&
        canExecute(roleNorm, "complete_by_company"))
      actions.push({
        key: "complete_by_company",
        label: "Concludi cartellino",
        icon: Lock,
        variant: "outline",
        className: "!border-suite-primary !text-suite-primary hover:!bg-suite-primary-light",
      });
    if (state === "to_correct" && canExecute(roleNorm, "submit_correction"))
      actions.push({
        key: "submit_correction",
        label: "Chiudi e invia correzione",
        icon: Send,
        variant: "accent",
      });
    if (state === "sent_to_company") {
      if (canExecute(roleNorm, "request_correction"))
        actions.push({ key: "request_correction", label: "Richiedi correzione", icon: AlertTriangle, variant: "warning" });
      if (canExecute(roleNorm, "approve"))
        actions.push({ key: "approve", label: "Approva", icon: CheckCircle2, variant: "accent" });
    }
    if (state === "approved") {
      if (canExecute(roleNorm, "reopen"))
        actions.push({ key: "reopen", label: "Riapri per correzione", icon: Unlock, variant: "warning" });
      if (canExecute(roleNorm, "close"))
        actions.push({ key: "close", label: "Chiudi mese", icon: Lock });
    }
    if (state === "closed" && canExecute(roleNorm, "reopen"))
      actions.push({ key: "reopen", label: "Riapri per correzione", icon: Unlock, variant: "warning" });
  }

  // v1.5.6 · L'azione "Concludi cartellino" (singolo dipendente) e' visivamente
  // legata al badge di stato: quando esiste, avvolgiamo Badge + azioni Concludi
  // in un sotto-container con bordo sottile, per rendere immediatamente chiaro
  // il legame stato → prossima azione.
  const hasCompleteAction = actions.some(
    (a) => a.key === "complete_by_studio" || a.key === "complete_by_company",
  );

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="mw-control">
      {hasCompleteAction ? (
        <div
          className="flex items-center gap-1.5 rounded-md border border-suite-neutral-200 bg-suite-neutral-50 pl-2 pr-1 py-0.5"
          data-testid="mw-state-action-group"
        >
          <SuiteBadge variant={STATE_VARIANT[state] || "default"} data-testid="mw-state-badge">
            {STATE_LABEL[state] || state}
          </SuiteBadge>
          <span className="text-suite-neutral-400 text-xs" aria-hidden="true">→</span>
          {actions
            .filter((a) => a.key === "complete_by_studio" || a.key === "complete_by_company")
            .map((a) => (
              <SuiteButton
                key={a.key}
                size="sm"
                variant={a.variant || "outline"}
                icon={a.icon}
                disabled={loading}
                onClick={() => openDialog(a.key)}
                data-testid={`mw-action-${a.key}`}
                className={a.className}
              >
                {a.label}
              </SuiteButton>
            ))}
        </div>
      ) : (
        <SuiteBadge variant={STATE_VARIANT[state] || "default"} data-testid="mw-state-badge">
          {STATE_LABEL[state] || state}
        </SuiteBadge>
      )}
      {actions
        .filter((a) => a.key !== "complete_by_studio" && a.key !== "complete_by_company")
        .map((a) => (
          <SuiteButton
            key={a.key}
            size="sm"
            variant={a.variant || "outline"}
            icon={a.icon}
            disabled={loading}
            onClick={() => openDialog(a.key)}
            data-testid={`mw-action-${a.key}`}
            className={a.className}
          >
            {a.label}
          </SuiteButton>
        ))}
      <Popover open={historyOpen} onOpenChange={(o) => { setHistoryOpen(o); if (o) loadEvents(); }}>
        <PopoverTrigger asChild>
          <SuiteButton
            size="sm" variant="ghost" icon={History}
            data-testid="mw-history-toggle"
            aria-label="Storico transizioni"
          />
        </PopoverTrigger>
        <PopoverContent side="bottom" align="end" className="w-96 max-h-96 overflow-auto">
          <div className="text-xs font-semibold mb-2">Storico transizioni</div>
          {events.length === 0 && (
            <div className="text-xs text-muted-foreground">Nessun evento</div>
          )}
          <ul className="space-y-2" data-testid="mw-history-list">
            {events.map((e) => (
              <li key={e.id} className="text-xs border-l-2 border-suite-border pl-2">
                <div className="font-medium">
                  {STATE_LABEL[e.to_state] || e.to_state}
                  <span className="text-muted-foreground"> ← {e.from_state ? (STATE_LABEL[e.from_state] || e.from_state) : "-"}</span>
                </div>
                <div className="text-muted-foreground">
                  {formatDateTimeIT(e.created_at, "—")} · {e.actor_role || "-"}
                </div>
                {e.reason && <div className="italic mt-1">« {e.reason} »</div>}
              </li>
            ))}
          </ul>
          {snapshotHash && (
            <div className="mt-3 pt-2 border-t text-[10px] text-muted-foreground font-mono break-all"
                 data-testid="mw-snapshot-details">
              snapshot v{snapshotVersion} · {snapshotHash.slice(0, 16)}…
            </div>
          )}
        </PopoverContent>
      </Popover>

      <Dialog open={!!dialogAction} onOpenChange={(o) => !o && setDialogAction(null)}>
        <DialogContent formDialog data-testid="mw-dialog">
          <DialogHeader>
            <DialogTitle>Conferma: {STATE_LABEL[
              dialogAction === "send" ? "sent_to_company"
              : dialogAction === "request_correction" ? "to_correct"
              : dialogAction === "approve" ? "approved"
              : dialogAction === "close" ? "closed"
              : dialogAction === "reopen" ? "to_correct"
              : dialogAction === "complete_by_studio" ? "closed"
              : dialogAction === "complete_by_company" ? "closed"
              : dialogAction === "submit_correction" ? "closed"
              : "draft"
            ]}</DialogTitle>
            <DialogDescription>
              {REASON_REQUIRED.has(dialogAction)
                ? "Motivazione obbligatoria (verrà registrata nell'audit)."
                : "Conferma la transizione."}
            </DialogDescription>
          </DialogHeader>
          {REASON_REQUIRED.has(dialogAction) && (
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Motivazione..."
              rows={3}
              maxLength={500}
              data-testid="mw-reason-input"
            />
          )}
          <DialogFooter>
            <SuiteButton variant="ghost" onClick={() => setDialogAction(null)}
              data-testid="mw-dialog-cancel">Annulla</SuiteButton>
            <SuiteButton
              disabled={loading || (REASON_REQUIRED.has(dialogAction) && !reason.trim())}
              onClick={() => doTransition(dialogAction, reason.trim() || null)}
              data-testid="mw-dialog-confirm"
            >Conferma</SuiteButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
